import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import Sidebar from '../components/Sidebar';
import PostDetailPanel from '../components/topic-tracking/PostDetailPanel';
import NotificationPanel from '../components/topic-tracking/NotificationPanel';
import apiClient from '../services/api/apiClient';
import { useToast } from '../components/ui/Toast/Toast';
import { useSelectedAccount } from '../contexts/SelectedAccountContext';

// Helper function to read cache synchronously
const readCache = (key, maxAge = 5 * 60 * 1000) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > maxAge;

    if (isExpired) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch (e) {
    console.error(`Failed to read cache ${key}:`, e);
    localStorage.removeItem(key);
    return null;
  }
};

function TopicTrackingPage() {
  const { toast, confirm } = useToast();
  const { selectedAccountId } = useSelectedAccount();

  // Debug: Monitor selectedAccountId changes
  useEffect(() => {
    console.log('TopicTrackingPage: selectedAccountId changed to:', selectedAccountId);
  }, [selectedAccountId]);

  // Initialize state with cached data (synchronous)
  const [keywords, setKeywords] = useState(() => readCache('topic_tracking_keywords_cache') || []);

  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchType, setSearchType] = useState('TOP'); // 'TOP' or 'RECENT'
  const [dateRange, setDateRange] = useState('all'); // 'today', '7days', '30days', 'all', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Notification panel state
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Onboarding setup state
  const [showOnboardingPrompt, setShowOnboardingPrompt] = useState(false);

  // Background refresh: Check and refresh expired cache
  useEffect(() => {
    loadKeywords();
  }, []);

  // Check onboarding status on mount
  useEffect(() => {
    if (keywords.length > 0) {
      checkOnboardingStatus();
    }
  }, [keywords.length]);

  // Load unread notification count
  useEffect(() => {
    if (selectedAccountId) {
      loadUnreadCount();

      // Poll every minute
      const interval = setInterval(() => {
        loadUnreadCount();
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [selectedAccountId]);

  // Load unread count
  const loadUnreadCount = async () => {
    if (!selectedAccountId) return;

    try {
      const response = await apiClient.get('/webhooks/notifications/unread-count', {
        params: { accountId: selectedAccountId }
      });

      if (response.data.success) {
        setUnreadCount(response.data.data.count);
      }
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  // Load keywords from API with cache
  const loadKeywords = async () => {
    const CACHE_KEY = 'topic_tracking_keywords_cache';
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    // Check if cache exists and is valid
    const cachedData = readCache(CACHE_KEY, CACHE_DURATION);
    if (cachedData) {
      console.log('Keywords cache is still valid, skipping API call');
      return; // Cache is valid, no need to update
    }

    // Cache is expired or missing, fetch from API
    try {
      console.log('Refreshing keywords from API');
      const response = await apiClient.get('/content/keywords');
      if (response.data.success) {
        const formattedKeywords = (response.data.data || []).map(kw => ({
          id: kw.id,
          keyword: kw.text,
          createdAt: kw.createdAt,
          updatedAt: kw.updatedAt,
          category: kw.category,
          usageCount: kw.usageCount
        }));

        // Update state
        setKeywords(formattedKeywords);

        // Save to cache
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: formattedKeywords,
          timestamp: Date.now()
        }));
      }
    } catch (error) {
      console.error('Failed to refresh keywords:', error);
    }
  };

  // Search by keyword
  const handleSearchKeyword = async (keyword) => {
    try {
      setSearching(true);
      setSelectedKeyword(keyword);
      setSelectedPost(null);
      setSearchInput(keyword.keyword);

      // Build query with search type and date range
      let url = `/threads/search?q=${encodeURIComponent(keyword.keyword)}&search_type=${searchType}&limit=50`;

      if (dateRange && dateRange !== 'all') {
        url += `&dateRange=${dateRange}`;

        if (dateRange === 'custom' && customStartDate && customEndDate) {
          url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
        }
      }

      const response = await apiClient.get(url);

      if (response.data.success && response.data.data) {
        setSearchResults(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to search keyword:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Handle search input
  const handleSearch = async () => {
    if (!searchInput.trim()) return;

    const keyword = { keyword: searchInput };
    handleSearchKeyword(keyword);
  };

  // Save current search as keyword
  const handleSaveKeyword = async () => {
    if (!searchInput.trim()) {
      toast.warning('請先輸入關鍵字');
      return;
    }

    // Check if keyword already exists
    if (keywords.some(kw => kw.keyword.toLowerCase() === searchInput.trim().toLowerCase())) {
      toast.warning('此關鍵字已存在');
      return;
    }

    try {
      const response = await apiClient.post('/content/keywords', {
        keyword: searchInput.trim(),
        category: 'topic-tracking'
      });

      if (response.data.success) {
        // Add new keyword directly to state for immediate display
        const newKeyword = {
          id: response.data.data?.id || Date.now().toString(),
          keyword: searchInput.trim(),
          createdAt: response.data.data?.createdAt || new Date().toISOString(),
          updatedAt: response.data.data?.updatedAt || new Date().toISOString(),
          category: 'topic-tracking',
          usageCount: 0
        };

        setKeywords([...keywords, newKeyword]);

        // Clear cache to force refresh on next load
        localStorage.removeItem('topic_tracking_keywords_cache');

        // Update cache with new keywords list
        localStorage.setItem('topic_tracking_keywords_cache', JSON.stringify({
          data: [...keywords, newKeyword],
          timestamp: Date.now()
        }));

        toast.success('關鍵字已儲存');

        // Show onboarding prompt if no account settings exist
        checkOnboardingStatus();
      }
    } catch (error) {
      console.error('Failed to save keyword:', error);
      toast.error(error.response?.data?.message || '儲存關鍵字失敗');
    }
  };

  // Check if user needs to complete onboarding
  const checkOnboardingStatus = async () => {
    try {
      // Check if user has completed account setup
      const hasCompletedSetup = localStorage.getItem('onboarding_completed');

      if (!hasCompletedSetup && keywords.length >= 1) {
        // Show prompt to start content setup
        setShowOnboardingPrompt(true);
      }
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
    }
  };

  // Start onboarding flow
  const handleStartOnboarding = () => {
    // Store current keywords for context
    localStorage.setItem('topic_tracking_keywords', JSON.stringify(keywords.map(k => k.keyword)));

    // Set Step1 as completed to allow access to Step2
    localStorage.setItem('onboarding_step1_completed', 'true');

    // Mark that we're coming from Topic Tracking
    localStorage.setItem('onboarding_from_topic_tracking', 'true');

    // Navigate to Step2 (Content Purpose)
    window.location.href = '/onboarding/step2';
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchResults([]);
    setSelectedKeyword(null);
    setSelectedPost(null);
  };

  // Select post
  const handleSelectPost = (post) => {
    setSelectedPost(post);
  };

  // Close detail panel
  const handleCloseDetail = () => {
    setSelectedPost(null);
  };

  // Delete keyword
  const handleDeleteKeyword = async (keywordId, e) => {
    e.stopPropagation(); // Prevent triggering the search when clicking delete

    const confirmed = await confirm({
      title: 'Delete Keyword',
      message: '確定要刪除此關鍵字嗎？',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive'
    });

    if (!confirmed) return;

    try {
      const response = await apiClient.delete(`/content/keywords/${keywordId}`);

      if (response.data.success) {
        // Remove from local state
        const updatedKeywords = keywords.filter(kw => kw.id !== keywordId);
        setKeywords(updatedKeywords);

        // Update cache with new keywords list
        localStorage.setItem('topic_tracking_keywords_cache', JSON.stringify({
          data: updatedKeywords,
          timestamp: Date.now()
        }));

        // If the deleted keyword was selected, clear search
        if (selectedKeyword?.id === keywordId) {
          setSelectedKeyword(null);
          setSearchResults([]);
          setSearchInput('');
        }

        toast.success('Keyword deleted successfully');
      }
    } catch (error) {
      console.error('Failed to delete keyword:', error);
      toast.error(error.response?.data?.message || '刪除關鍵字失敗，請稍後再試');
    }
  };

  return (
    <div className="h-screen flex bg-slate-100 overflow-hidden">
      {/* Sidebar */}
      <Sidebar activePage="topic-tracking" />

      {/* Main Content */}
        <div className={`flex-1 overflow-hidden ${selectedPost ? 'flex' : 'flex flex-col'}`}>
        <div className={`${selectedPost ? 'w-[60%]' : 'w-full'} min-w-0 flex flex-col overflow-hidden`}>
          {/* Header Section with Search and Tags */}
          <div className="bg-gradient-to-br from-white via-slate-50 to-blue-50 px-10 py-10 m-6 rounded-3xl border border-slate-200/70 shadow-sm">
            {/* Title with Notification Button */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Topic Tracking</h1>
                <p className="text-sm text-slate-500 mt-1">集中整理關鍵字與貼文動態，快速掌握話題脈絡。</p>
              </div>

            {/* Notification Button */}
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-3 bg-white rounded-full shadow-sm hover:shadow-md transition-all border border-gray-200 hover:border-blue-300"
            >
              <Icon icon="mdi:bell" width={24} className="text-gray-700" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* Search Bar */}
            <div className="flex items-center gap-3 max-w-3xl mb-4">
              <div className="relative flex-1">
                <div className="absolute left-5 top-1/2 -translate-y-1/2">
                  <Icon icon="mdi:magnify" width={24} className="text-blue-600" />
                </div>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search topics..."
                  className="w-full bg-white border-2 border-blue-200 rounded-full px-14 py-4 text-base focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all shadow-md"
                />
                {searchInput && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-5 top-1/2 -translate-y-1/2 hover:bg-blue-50 rounded-full p-1.5 transition-colors"
                  >
                    <Icon icon="mdi:close" width={20} className="text-blue-600" />
                  </button>
                )}
              </div>

              {/* Save Keyword Button */}
              {searchInput && !keywords.some(kw => kw.keyword.toLowerCase() === searchInput.trim().toLowerCase()) && (
                <button
                  onClick={handleSaveKeyword}
                  className="px-6 py-4 bg-blue-500 text-white rounded-full font-medium shadow-md hover:bg-blue-600 hover:shadow-lg transition-all flex items-center gap-2 whitespace-nowrap"
                  title="將此關鍵字儲存到常用列表"
                >
                  <Icon icon="mdi:bookmark-plus" width={20} />
                  儲存
                </button>
              )}
            </div>

          {/* Search Type Toggle */}
            <div className="flex flex-wrap items-center gap-6 mb-4">
              {/* Search Type Toggle */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Sort By:</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSearchType('TOP')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      searchType === 'TOP'
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <Icon icon="mdi:fire" width={18} />
                    Top Posts
                  </button>
                  <button
                    onClick={() => setSearchType('RECENT')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      searchType === 'RECENT'
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <Icon icon="mdi:clock-outline" width={18} />
                    Recent Posts
                  </button>
                </div>
              </div>
            </div>

          {/* Date Range Filter */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="text-sm font-medium text-gray-700">Time Range:</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'All Time' },
                  { value: 'today', label: 'Today' },
                  { value: '7days', label: 'Last 7 Days' },
                  { value: '30days', label: 'Last 30 Days' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setDateRange(option.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      dateRange === option.value
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {/* Custom Date Inputs */}
              {dateRange === 'custom' && (
                <div className="flex items-center gap-2 ml-2">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              )}
            </div>

          {/* Tags/Keywords */}
            <div className="flex flex-wrap gap-3">
              {loading ? (
                <div className="text-gray-500 text-sm">Loading keywords...</div>
              ) : keywords.length > 0 ? (
                keywords.map((keyword) => (
                  <div
                    key={keyword.id}
                    className="relative group"
                  >
                    <button
                      onClick={() => handleSearchKeyword(keyword)}
                      className={`px-4 py-2 pr-8 rounded-full text-sm font-medium transition-all ${
                        selectedKeyword?.id === keyword.id
                          ? 'bg-blue-500 text-white shadow-md'
                          : 'bg-white/60 text-gray-700 hover:bg-white hover:shadow-sm border border-gray-200'
                      }`}
                    >
                      {keyword.keyword}
                    </button>
                    <button
                      onClick={(e) => handleDeleteKeyword(keyword.id, e)}
                      className={`absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full transition-all ${
                        selectedKeyword?.id === keyword.id
                          ? 'text-white hover:bg-white/20'
                          : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                      }`}
                      title="刪除關鍵字"
                    >
                      <Icon icon="mdi:close" width={16} />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-sm">No keywords yet</div>
              )}
            </div>
          </div>

        {/* Posts Grid */}
          <div className="flex-1 overflow-auto px-8 pb-8">
            <div className="rounded-3xl border border-slate-200/80 bg-white shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Matched Posts</div>
                  <div className="text-lg font-semibold text-slate-900 mt-1">搜尋結果</div>
                </div>
                {selectedKeyword && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                    {selectedKeyword.keyword}
                  </span>
                )}
              </div>

              <div className={`mt-6 grid gap-3 ${selectedPost ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
                {searching ? (
                  <div className="flex items-center justify-center py-16 col-span-full">
                    <div className="text-center">
                      <Icon icon="mdi:loading" width={32} className="text-blue-500 animate-spin mx-auto mb-3" />
                      <p className="text-gray-600 text-sm">Searching posts...</p>
                    </div>
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((post) => (
                    <div
                      key={post.id}
                      onClick={() => handleSelectPost(post)}
                      className={`rounded-2xl border p-4 cursor-pointer transition-all ${
                        selectedPost?.id === post.id
                          ? 'border-slate-900/70 bg-slate-50 shadow-md'
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {post.profile_picture_url ? (
                            <img
                              src={post.profile_picture_url}
                              alt={post.username}
                              className="w-10 h-10 rounded-xl object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 font-semibold">
                              {post.username?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">
                              {post.username || 'Unknown User'}
                            </div>
                            <div className="text-xs text-slate-500">
                              {new Date(post.timestamp).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold uppercase text-emerald-600">
                          Active
                        </span>
                      </div>
                      <p className="mt-3 text-sm text-slate-600 line-clamp-2">
                        {post.text || 'No content available'}
                      </p>
                      <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                        <span>點擊查看詳情</span>
                        <Icon icon="mdi:chevron-right" width={16} />
                      </div>
                    </div>
                  ))
                ) : selectedKeyword ? (
                  <div className="flex items-center justify-center py-16 col-span-full">
                    <div className="text-center">
                      <Icon icon="mdi:inbox" width={48} className="text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">No posts found for "{selectedKeyword.keyword}"</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-16 col-span-full">
                    <div className="text-center">
                      <Icon icon="mdi:magnify" width={48} className="text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Select a topic or search to view posts</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {selectedPost && (
          <div className="w-[40%] min-w-[360px] max-w-[520px] flex flex-col pr-6 pb-6 pt-6">
            <div className="rounded-3xl border border-slate-200/80 bg-white shadow-sm overflow-hidden flex-1">
              <PostDetailPanel
                post={selectedPost}
                accountId={selectedAccountId}
                onReplySent={() => {
                  console.log('Reply sent successfully!');
                }}
                onClose={handleCloseDetail}
                className="h-full"
              />
            </div>
          </div>
        )}
      </div>

      {/* Notification Panel (Sliding from right) */}
      {showNotifications && (
        <NotificationPanel
          accountId={selectedAccountId}
          onClose={() => {
            setShowNotifications(false);
            loadUnreadCount(); // Refresh count when closing
          }}
        />
      )}

      {/* Onboarding Setup Prompt */}
      {showOnboardingPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 relative">
            {/* Close button */}
            <button
              onClick={() => setShowOnboardingPrompt(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Icon icon="mdi:close" width={24} className="text-gray-400" />
            </button>

            {/* Icon */}
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Icon icon="mdi:rocket-launch" width={32} className="text-white" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
              準備好打造你的內容策略了嗎？
            </h2>

            {/* Description */}
            <p className="text-gray-600 text-center mb-8">
              你已經設定好追蹤關鍵字！接下來，讓我們透過幾個簡單的問題，幫你建立個人化的內容策略。
            </p>

            {/* Keywords preview */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
              <div className="text-sm text-gray-700 mb-2 font-medium">你的追蹤關鍵字：</div>
              <div className="flex flex-wrap gap-2">
                {keywords.slice(0, 5).map((kw) => (
                  <span key={kw.id} className="px-3 py-1 bg-blue-500 text-white rounded-full text-sm font-medium">
                    {kw.keyword}
                  </span>
                ))}
                {keywords.length > 5 && (
                  <span className="px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-sm font-medium">
                    +{keywords.length - 5} 個
                  </span>
                )}
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-3 mb-8">
              {[
                { icon: 'mdi:target', text: '定義你的內容目標與受眾' },
                { icon: 'mdi:palette', text: '選擇適合的內容風格與格式' },
                { icon: 'mdi:chart-line', text: '建立個人化的發文策略' }
              ].map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon icon={benefit.icon} width={18} className="text-blue-600" />
                  </div>
                  <span className="text-sm text-gray-700">{benefit.text}</span>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowOnboardingPrompt(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
              >
                稍後設定
              </button>
              <button
                onClick={handleStartOnboarding}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                開始設定
                <Icon icon="mdi:arrow-right" width={20} />
              </button>
            </div>

            {/* Footer note */}
            <p className="text-xs text-gray-400 text-center mt-4">
              只需 2-3 分鐘，6 個簡單問題
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default TopicTrackingPage;
